import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, FloatButton, message as antMessage } from 'antd';
import { MessageOutlined } from '@ant-design/icons';
import { useGetIdentity, useTranslate } from '@refinedev/core';
import { ChatMessage, SupportWidgetCore } from '../core/support-widget-core';
import { ComposerPill } from './ComposerPill';
import { MessengerPanel } from './MessengerPanel';
import { MessageThread, HistoryState } from './MessageThread';
import './widget-messenger.css';

/** ECU localStorage key for the current user JWT (matches authProvider.ts TOKEN_KEY = "token") */
const TOKEN_KEY = 'token';

/** Mirrors the hub's allowlist (AttachmentsService, D-30) — client-side gate before presign. */
const ATTACHMENT_MIME_ALLOWLIST = [
    'image/png',
    'image/jpeg',
    'image/gif',
    'image/webp',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/plain',
    'text/csv',
    'application/zip',
];

/** 10MB cap (D-30) — mirrors ATTACHMENT_MAX_BYTES in attachments.service.ts. */
const ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;

type SubmitState = 'idle' | 'sending' | 'error';

/**
 * Per-identity localStorage key for the active conversation (D-21, Pitfall 5 — NEVER
 * un-scoped, a shared browser must not leak one user's conversation to another).
 */
const conversationStorageKey = (identityId: string): string =>
    `support_conversation_${identityId}`;

interface SupportWidgetInnerProps {
    apiUrl: string;
    hubUrl: string;
    appVersion?: string;
}

/**
 * Customer-facing help widget (D-04/D-05) — Intercom-style floating messenger.
 *
 * - Capture starts on mount (continuous, not on drawer open — D-10).
 * - Opening the panel fires NO network request (D-01).
 * - The first composer send triggers: POST /hub-ticket → /customer WS → session.create (D-02).
 * - All visible strings via useTranslate (HELP-03, CLAUDE.md).
 */
export const SupportWidgetInner: React.FC<SupportWidgetInnerProps> = ({
    apiUrl,
    hubUrl,
    appVersion = '',
}) => {
    const translate = useTranslate();
    const { data: identity } = useGetIdentity<{
        id: string;
        role?: string;
        tenantSlug?: string;
    }>();

    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState('');
    const [submitState, setSubmitState] = useState<SubmitState>('idle');
    const [conversationStarted, setConversationStarted] = useState(false);
    const [historyState, setHistoryState] = useState<HistoryState>('idle');
    const [messagesById, setMessagesById] = useState<Map<number, ChatMessage>>(new Map());
    const [agentTyping, setAgentTyping] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [pendingAttachmentIds, setPendingAttachmentIds] = useState<number[]>([]);
    const [uploading, setUploading] = useState(false);
    const threadRef = useRef<HTMLDivElement>(null);
    const openRef = useRef(open);
    const coreRef = useRef<SupportWidgetCore | null>(null);

    useEffect(() => {
        if (!identity?.id) return;

        const context: Record<string, unknown> = {
            route: window.location.pathname,
            appVersion,
            userId: identity.id,
            tenantSlug: identity.tenantSlug ?? '',
        };

        const core = new SupportWidgetCore({
            apiUrl,
            hubUrl,
            getToken: () => localStorage.getItem(TOKEN_KEY),
            context,
        });

        core.start();
        coreRef.current = core;

        setConversationStarted(false);
        setMessagesById(new Map());
        setHistoryState('idle');

        core.onHistory((merged) => {
            setMessagesById((prev) => {
                const next = new Map(prev);
                for (const msg of merged) {
                    next.set(msg.id, msg);
                }
                return next;
            });
        });

        const storedId = localStorage.getItem(conversationStorageKey(identity.id));
        if (storedId) {
            const conversationId = Number(storedId);
            if (Number.isFinite(conversationId)) {
                setHistoryState('loading');
                core.reconnect(conversationId)
                    .then((merged) => {
                        setMessagesById((prev) => {
                            const next = new Map(prev);
                            for (const msg of merged) {
                                next.set(msg.id, msg);
                            }
                            return next;
                        });
                        setConversationStarted(true);
                        setHistoryState('idle');
                    })
                    .catch(() => {
                        setHistoryState('error');
                    });
            }
        }

        return () => {
            core.dispose();
            coreRef.current = null;
        };
    }, [identity?.id, identity?.tenantSlug, apiUrl, hubUrl, appVersion]);

    useEffect(() => {
        const core = coreRef.current;
        if (!core || !conversationStarted) return;

        const unsubscribe = core.onMessage((msg) => {
            setMessagesById((prev) => {
                const next = new Map(prev);
                next.set(msg.id, msg);
                return next;
            });
            if (!openRef.current && msg.participant.authorType === 'agent') {
                setUnreadCount((prev) => prev + 1);
            }
        });

        return unsubscribe;
    }, [conversationStarted]);

    useEffect(() => {
        const core = coreRef.current;
        if (!core || !conversationStarted) return;

        const unsubscribe = core.onTyping((typing) => setAgentTyping(typing));
        return unsubscribe;
    }, [conversationStarted]);

    useEffect(() => {
        const core = coreRef.current;
        if (!core || !conversationStarted) return;

        const unsubscribe = core.onRead((payload) => {
            setMessagesById((prev) => {
                const existing = prev.get(payload.messageId);
                if (!existing || existing.isRead === payload.isRead) {
                    return prev;
                }
                const next = new Map(prev);
                next.set(payload.messageId, { ...existing, isRead: payload.isRead });
                return next;
            });
        });
        return unsubscribe;
    }, [conversationStarted]);

    useEffect(() => {
        const core = coreRef.current;
        if (!core || !conversationStarted || !open) return;

        for (const msg of messagesById.values()) {
            if (msg.participant.authorType === 'agent' && !msg.isRead) {
                core.markRead(msg.id);
            }
        }
    }, [conversationStarted, open, messagesById]);

    useEffect(() => {
        const el = threadRef.current;
        if (el) {
            el.scrollTop = el.scrollHeight;
        }
    }, [messagesById, agentTyping]);

    useEffect(() => {
        openRef.current = open;
        if (open) {
            setUnreadCount(0);
        }
    }, [open]);

    const handleClose = useCallback(() => {
        setOpen(false);
    }, []);

    const handleSend = useCallback(async () => {
        const core = coreRef.current;
        const content = draft.trim();
        const hasAttachments = pendingAttachmentIds.length > 0;
        if (!core || (!content && !hasAttachments)) return;

        if (!conversationStarted) {
            if (!content) return;
            setSubmitState('sending');
            try {
                const conversationId = await core.submit(content);
                if (identity?.id) {
                    localStorage.setItem(
                        conversationStorageKey(identity.id),
                        String(conversationId),
                    );
                }
                setSubmitState('idle');
                setConversationStarted(true);
                setDraft('');
            } catch {
                setSubmitState('error');
            }
            return;
        }

        setDraft('');
        const attachmentIds = pendingAttachmentIds;
        setPendingAttachmentIds([]);
        await core.sendMessage(content, attachmentIds.length ? attachmentIds : undefined);
    }, [draft, conversationStarted, identity?.id, pendingAttachmentIds]);

    const handleUpload = useCallback(
        async (file: File): Promise<boolean> => {
            const core = coreRef.current;
            if (!core || !conversationStarted) {
                return false;
            }
            if (!ATTACHMENT_MIME_ALLOWLIST.includes(file.type)) {
                antMessage.error(translate('support.help.chat.fileTypeRejected'));
                return false;
            }
            if (file.size > ATTACHMENT_MAX_BYTES) {
                antMessage.error(translate('support.help.chat.fileTooLarge'));
                return false;
            }
            setUploading(true);
            try {
                const result = await core.uploadAttachment(file);
                if (!result.ok) {
                    antMessage.error(translate('support.help.chat.uploadFailed'));
                    return false;
                }
                setPendingAttachmentIds((prev) => [...prev, result.attachmentId]);
                return false;
            } finally {
                setUploading(false);
            }
        },
        [conversationStarted, translate],
    );

    const getAttachmentDownloadUrl = useCallback(
        (attachmentId: number) =>
            coreRef.current?.getAttachmentDownloadUrl(attachmentId) ??
            Promise.resolve({ ok: false as const, error: 'no active conversation' }),
        [],
    );

    if (!identity) {
        return null;
    }

    const isSending = submitState === 'sending';
    const isError = submitState === 'error';

    const messages = Array.from(messagesById.values()).sort((a, b) => {
        const byCreatedAt = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        return byCreatedAt !== 0 ? byCreatedAt : a.id - b.id;
    });

    return (
        <>
            {!open ? (
                <Badge count={unreadCount}>
                    <FloatButton
                        icon={<MessageOutlined />}
                        type="primary"
                        tooltip={translate('support.help.open')}
                        onClick={() => setOpen(true)}
                    />
                </Badge>
            ) : null}

            <MessengerPanel
                open={open}
                title={translate('support.help.title')}
                subtitle={translate('support.help.chat.subtitle')}
                closeLabel={translate('support.help.chat.close')}
                ariaLabel={translate('support.help.title')}
                onClose={handleClose}
            >
                <MessageThread
                    threadRef={threadRef}
                    historyState={historyState}
                    conversationStarted={conversationStarted}
                    messages={messages}
                    agentTyping={agentTyping}
                    isError={isError}
                    translate={translate}
                    getAttachmentDownloadUrl={getAttachmentDownloadUrl}
                />
                <ComposerPill
                    draft={draft}
                    onDraftChange={setDraft}
                    onSend={() => void handleSend()}
                    onTyping={() => coreRef.current?.notifyTyping()}
                    onUpload={handleUpload}
                    pendingAttachmentIds={pendingAttachmentIds}
                    onRemoveAttachment={(id) =>
                        setPendingAttachmentIds((prev) =>
                            prev.filter((existing) => existing !== id),
                        )
                    }
                    conversationStarted={conversationStarted}
                    isSending={isSending}
                    uploading={uploading}
                    placeholder={translate('support.help.chat.placeholder')}
                    attachLabel={translate('support.help.chat.attach')}
                    sendLabel={translate('support.help.chat.send')}
                    poweredByLabel={translate('support.help.chat.poweredBy')}
                />
            </MessengerPanel>
        </>
    );
};
